import type { AppCatalogEntry, AppGroupId } from "./catalog";

export const CATALOG_ORDER_STORAGE_KEY = "me-site.catalog-order.v1";

export type CatalogOrder = Record<AppGroupId, string[]>;

const groupIds: AppGroupId[] = ["daily", "discover", "operate"];

export function defaultCatalogOrder(entries: readonly AppCatalogEntry[]): CatalogOrder {
  return {
    daily: entries.filter((entry) => entry.group === "daily").map((entry) => entry.id),
    discover: entries.filter((entry) => entry.group === "discover").map((entry) => entry.id),
    operate: entries.filter((entry) => entry.group === "operate").map((entry) => entry.id),
  };
}

/** Keep known stored IDs, discard stale ones, and append newly added apps. */
export function normalizeCatalogOrder(
  entries: readonly AppCatalogEntry[],
  stored?: Partial<Record<AppGroupId, unknown>>,
): CatalogOrder {
  const defaults = defaultCatalogOrder(entries);
  return Object.fromEntries(groupIds.map((group) => {
    const known = new Set(defaults[group]);
    const storedGroup = stored?.[group];
    const candidates = Array.isArray(storedGroup)
      ? storedGroup.filter((id): id is string => typeof id === "string" && known.has(id))
      : [];
    const unique = [...new Set(candidates)];
    const missing = defaults[group].filter((id) => !unique.includes(id));
    return [group, [...unique, ...missing]];
  })) as CatalogOrder;
}

export function readCatalogOrder(
  storage: Pick<Storage, "getItem">,
  entries: readonly AppCatalogEntry[],
): CatalogOrder {
  try {
    const value = storage.getItem(CATALOG_ORDER_STORAGE_KEY);
    return normalizeCatalogOrder(entries, value ? JSON.parse(value) as Record<AppGroupId, unknown> : undefined);
  } catch {
    return defaultCatalogOrder(entries);
  }
}

export function writeCatalogOrder(
  storage: Pick<Storage, "setItem">,
  order: CatalogOrder,
): void {
  storage.setItem(CATALOG_ORDER_STORAGE_KEY, JSON.stringify(order));
}

export function moveCatalogEntry(
  order: CatalogOrder,
  entry: AppCatalogEntry,
  direction: -1 | 1,
): CatalogOrder {
  const current = [...order[entry.group]];
  const index = current.indexOf(entry.id);
  const destination = index + direction;
  if (index < 0 || destination < 0 || destination >= current.length) return order;
  [current[index], current[destination]] = [current[destination], current[index]];
  return { ...order, [entry.group]: current };
}

export function applyCatalogOrder(
  entries: readonly AppCatalogEntry[],
  order: CatalogOrder,
): AppCatalogEntry[] {
  return [...entries].sort((left, right) => {
    if (left.group !== right.group) return 0;
    return order[left.group].indexOf(left.id) - order[right.group].indexOf(right.id);
  });
}
